<template>
  <div class="side-tab-container">
    <!-- Tab Handle -->
    <div 
      class="tab-handle" 
      @click="toggleTab"
      :class="{ 'open': isOpen }"
    >
      <span class="tab-text">{{ isOpen ? '◀' : '▶' }}</span>
      <span class="tab-label">Menu</span>
    </div>
    
    <!-- Side Panel -->
    <div class="side-panel" :class="{ 'open': isOpen }">
      <div class="panel-header">
        <h3>Navigation</h3>
        <button class="close-btn" @click="closeTab">×</button>
      </div>
      
      <div class="panel-content">
        <div class="directory-list">
          <router-link 
            to="/tutorial"
            class="directory-item" 
            :class="{ 'active': $route.name === 'tutorial' }"
            @click="handleNavigation"
          >
            <span class="directory-icon">📚</span>
            <span class="directory-name">Tutorial</span>
          </router-link>
          
          <router-link 
            to="/presentation"
            class="directory-item"
            :class="{ 'active': $route.name === 'presentation' }"
            @click="handleNavigation"
          >
            <span class="directory-icon">📊</span>
            <span class="directory-name">Presentation</span>
          </router-link>
          
          <router-link 
            to="/application"
            class="directory-item"
            :class="{ 'active': $route.name === 'application' }"
            @click="handleNavigation"
          >
            <span class="directory-icon">⚙️</span>
            <span class="directory-name">Application</span>
          </router-link>
          
          <router-link
            to="/contact"
            class="directory-item"
            :class="{ 'active': $route.name === 'contact' }"
            @click="handleNavigation"
          >
            <span class="directory-icon">📧</span>
            <span class="directory-name">Contact</span>
          </router-link>

          <router-link
            to="/communications"
            class="directory-item"
            :class="{ 'active': $route.name === 'communications' }"
            @click="handleNavigation"
          >
            <span class="directory-icon">📢</span>
            <span class="directory-name">Communications</span>
          </router-link>

          <router-link
            to="/legal"
            class="directory-item"
            :class="{ 'active': $route.name === 'legal' }"
            @click="handleNavigation"
          >
            <span class="directory-icon">⚖️</span>
            <span class="directory-name">Legal notices</span>
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Overlay for mobile -->
    <div 
      v-if="isOpen" 
      class="overlay" 
      @click="closeTab"
    ></div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const isOpen = ref(false);

const toggleTab = () => {
  isOpen.value = !isOpen.value;
};

const closeTab = () => {
  isOpen.value = false;
};

const handleNavigation = () => {
  // Close tab on mobile after selection
  if (window.innerWidth <= 768) {
    closeTab();
  }
};
</script>

<style scoped>
.side-tab-container {
  position: fixed;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  z-index: 1000;
  pointer-events: none;
}

.tab-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 35px;
  height: 80px;
  background: #007bff;
  color: white;
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0 8px 8px 0;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  user-select: none;
  z-index: 1001;
}

.tab-handle:hover {
  background: #0056b3;
  transform: translateX(1px);
}

.tab-handle.open {
  background: #0056b3;
  left: 280px;
}

.tab-text {
  font-size: 12px;
  margin-bottom: 4px;
}

.tab-label {
  font-size: 10px;
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

.side-panel {
  width: 280px;
  height: 520px;
  background: white;
  border-radius: 0 8px 8px 0;
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.15);
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  overflow: hidden;
  pointer-events: none;
}

.side-panel.open {
  transform: translateX(0);
  pointer-events: auto;
}

.panel-header {
  background: #f8f9fa;
  padding: 15px 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #e9ecef;
  color: #495057;
}

.panel-content {
  padding: 20px;
}

.directory-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.directory-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  text-decoration: none;
  color: inherit;
}

.directory-item:hover {
  background: #f8f9fa;
  border-color: #e9ecef;
  text-decoration: none;
  color: inherit;
}

.directory-item.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.directory-item.active:hover {
  background: #0056b3;
  color: white;
}

.directory-icon {
  margin-right: 12px;
  font-size: 16px;
}

.directory-name {
  font-size: 14px;
  font-weight: 500;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  z-index: -1;
  display: none;
  pointer-events: auto;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .side-panel {
    width: 260px;
    height: 470px;
  }
  
  .tab-handle.open {
    left: 260px;
  }
  
  .overlay {
    display: block;
  }
  
  .tab-handle {
    width: 35px;
    height: 70px;
  }
  
  .tab-label {
    font-size: 9px;
  }
}

@media (max-width: 480px) {
  .side-panel {
    width: 240px;
  }
  
  .tab-handle.open {
    left: 240px;
  }
  
  .panel-content {
    padding: 15px;
  }
}
</style> 